// icons.tsx — the app's single icon registry, backed by lucide-react.
// Every glyph renders through the `Icon` component below; the name → glyph
// mapping is documented in docs/icon-registry.md (one concept = one glyph).
// `lucide-react` may ONLY be imported here (enforced by eslint no-restricted-imports).
import type { CSSProperties } from 'react';
import type { LucideIcon } from 'lucide-react';
import {
  ArrowDownToLine,
  ArrowRight,
  Ban,
  Barcode,
  Bell,
  Box,
  Boxes,
  Calendar,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CircleCheck,
  CircleX,
  Clipboard,
  Clock,
  Download,
  Ellipsis,
  Eye,
  FileCheck,
  FileText,
  Flag,
  Funnel,
  History,
  Info,
  Key,
  Layers,
  LayoutGrid,
  Lock,
  LogOut,
  MapPin,
  Minus,
  Moon,
  Move,
  Package,
  Pill,
  Plus,
  RefreshCw,
  Save,
  ScanLine,
  Search,
  Settings,
  Shield,
  ShieldCheck,
  SlidersHorizontal,
  SlidersVertical,
  Snowflake,
  SquareCheckBig,
  SquarePen,
  Store,
  Sun,
  Thermometer,
  Trash,
  Truck,
  TriangleAlert,
  Upload,
  User,
  Users,
  X,
  Zap,
} from 'lucide-react';

const LUCIDE_ICONS = {
  moon: Moon,
  sun: Sun,
  dashboard: LayoutGrid,
  products: Package,
  batches: Boxes,
  locations: MapPin,
  adjustments: SlidersVertical,
  reconciliation: SquareCheckBig,
  alerts: TriangleAlert,
  importexport: ArrowDownToLine,
  settings: Settings,
  search: Search,
  bell: Bell,
  plus: Plus,
  chevdown: ChevronDown,
  chevright: ChevronRight,
  chevleft: ChevronLeft,
  check: Check,
  clock: Clock,
  filter: Funnel,
  download: Download,
  upload: Upload,
  dots: Ellipsis,
  edit: SquarePen,
  thermometer: Thermometer,
  shield: Shield,
  shieldCheck: ShieldCheck,
  box: Box,
  pill: Pill,
  close: X,
  lock: Lock,
  eye: Eye,
  history: History,
  truck: Truck,
  checkcircle: CircleCheck,
  xcircle: CircleX,
  user: User,
  users: Users,
  store: Store,
  snowflake: Snowflake,
  trash: Trash,
  move: Move,
  ban: Ban,
  info: Info,
  barcode: Barcode,
  clipboard: Clipboard,
  filecheck: FileCheck,
  fileText: FileText,
  save: Save,
  calendar: Calendar,
  key: Key,
  sliders: SlidersHorizontal,
  arrowright: ArrowRight,
  scan: ScanLine,
  layers: Layers,
  refresh: RefreshCw,
  minus: Minus,
  zap: Zap,
  flag: Flag,
  logout: LogOut,
} as const satisfies Record<string, LucideIcon>;

// Glyphs with no in-family Lucide twin keep their hand-rolled SVG paths.
const CUSTOM_PATHS = {
  multistore:
    '<path d="M3 9l1-4h16l1 4M5 9v10a1 1 0 0 0 1 1h3v-6h6v6h3a1 1 0 0 0 1-1V9"/><path d="M3 9h18"/>',
} as const;

export type IconName = keyof typeof LUCIDE_ICONS | keyof typeof CUSTOM_PATHS;

export interface IconProps {
  name: IconName;
  size?: number;
  color?: string;
  stroke?: number;
  style?: CSSProperties;
}

export function Icon({ name, size = 20, color = 'currentColor', stroke = 2, style }: IconProps) {
  const Glyph = (LUCIDE_ICONS as Record<string, LucideIcon | undefined>)[name];
  if (Glyph) {
    return <Glyph size={size} color={color} strokeWidth={stroke} style={style} />;
  }
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={stroke}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={style}
      dangerouslySetInnerHTML={{
        __html: CUSTOM_PATHS[name as keyof typeof CUSTOM_PATHS] || '',
      }}
    />
  );
}
